/**
 * Hosts whose links this app is allowed to take over. The manifest already narrows what the
 * system hands us (teamplay.gg only), so this is the second line: a URL arriving from anywhere
 * else must never be turned into in-app navigation.
 *
 * dev is here so a native dev build can open a link from the environment it talks to; the
 * manifest doesn't claim it, because its assetlinks.json sits behind basic auth and could
 * never verify.
 */
const APP_LINK_HOSTS = new Set(['teamplay.gg', 'www.teamplay.gg', 'dev.teamplay.gg']);

/**
 * The in-app path for an incoming Android App Link / iOS Universal Link, or null when the URL
 * is not one of ours to handle.
 *
 * Only the path travels: the native shell serves the app from https://localhost, so loading the
 * absolute URL would leave the app and open the website instead.
 *
 * The session-chat scheme is also ours. Other custom links belong to OAuth, push-tap and
 * call listeners and must not be consumed here.
 */
export const appLinkPath = (url: string | null | undefined): string | null => {
  if (!url) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  if (
    parsed.protocol === 'gg.teamplay.twa:' &&
    parsed.host === 'session' &&
    !parsed.pathname.replaceAll('/', '')
  ) {
    const postId = parsed.searchParams.get('postId');
    return postId && /^[a-zA-Z0-9_-]{1,128}$/.test(postId) ? `/posts/${postId}` : null;
  }
  if (
    parsed.protocol === 'gg.teamplay.twa:' &&
    (parsed.host === 'session-chat' || parsed.host === 'call-chat') &&
    !parsed.pathname.replaceAll('/', '')
  ) {
    const chatId = parsed.searchParams.get('chatId');
    return chatId && /^[a-zA-Z0-9_-]{1,128}$/.test(chatId) ? `/dashboard/chats/${chatId}` : null;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return null;
  }

  if (!APP_LINK_HOSTS.has(parsed.host)) {
    return null;
  }

  return `${parsed.pathname}${parsed.search}`;
};
